import type {
  AllyTurnRequest,
  AllyTurnResponse,
  ChartFact,
  MemorySuggestion,
} from '../types/ai.js';

/**
 * Deterministic, chart-grounded mock Ally.
 *
 * Used by BOTH the Firebase Functions mock provider and the app's demo mode
 * (so the flow is demonstrable with zero credentials, and the grounding
 * contract is exercised end-to-end). It only ever references facts supplied
 * in the request — exactly the rule real providers must follow.
 */

function factsBy(facts: ChartFact[], ...prefixes: string[]): ChartFact[] {
  return facts.filter((f) => prefixes.some((p) => f.id.startsWith(p) || f.id.includes(p)));
}

function cite(used: Set<string>, fact?: ChartFact): string {
  if (!fact) return '';
  used.add(fact.id);
  return fact.value;
}

const SAFETY_NOTE =
  'A quick note: astrology here is an interpretive lens on your calculated chart, not a proven prediction — I will never promise outcomes.';

export function mockAllyTurn(req: AllyTurnRequest): AllyTurnResponse {
  const lastUser = [...req.messages].reverse().find((m) => m.role === 'user')?.text ?? '';
  const q = lastUser.toLowerCase();
  const used = new Set<string>();
  const parts: string[] = [];
  const suggestions: MemorySuggestion[] = [];

  // Persona = which voice answers. Absent means Jyotish: Astrally is
  // Indian-first. It selects vocabulary and which facts lead — never what was
  // calculated, which is identical either way (both fact sets are present).
  const west = (req.persona ?? 'jyotish') === 'western';

  const moonJ = req.facts.find((f) => f.id === 'jyotish.moon.position' || f.id === 'stable.planet.moon.nakshatra');
  const moonRange = req.facts.find((f) => f.id.startsWith('varying.planet.moon'));
  const lagna = req.facts.find((f) => f.id === 'jyotish.lagna');
  const dasha = req.facts.find((f) => f.id === 'jyotish.dasha.current');
  const dashaPoss = req.facts.find((f) => f.id === 'jyotish.dasha.possibilities');
  const ascUnavailable = req.facts.find((f) => f.id === 'unavailable.angles.ascendant');

  // Western counterparts. Never cross-fall-back to a sidereal fact: quoting a
  // rashi as a tropical sign would be flatly wrong, not merely off-voice.
  const moonW = req.facts.find((f) => f.id === 'western.moon.position' || f.id === 'stable.planet.moon.tropicalSign');
  const sunW = req.facts.find((f) => f.id === 'western.sun.position' || f.id === 'stable.planet.sun.tropicalSign');
  const ascW = req.facts.find((f) => f.id === 'western.ascendant');
  const mcW = req.facts.find((f) => f.id === 'western.mc');

  const isRec = !!req.recommendationContext;
  const topic = isRec && (q.includes('why') || q.includes('who') || q.includes('person'))
    ? 'recommendation'
    : q.match(/\b(?:birth time|rectif\w*|uncertain\w*|time window|unknown|rising sign)\b/) ? 'uncertainty'
    : q.match(/\b(?:career|job|work|business|profession)\b/) ? 'career'
    : q.match(/\b(?:marriage|relationship|partner|love|romance)\b/) ? 'relationship'
    : q.match(/\b(?:money|finance|wealth|income)\b/) ? 'money'
    : q.match(/\b(?:dasha|period|phase|timing|when|future)\b/) ? 'timing'
    : q.match(/\b(?:transit\w*|gochar|now|current|today)\b/) ? 'transits'
    : q.match(/\b(?:jyotish|western|difference|sidereal|tropical)\b/) ? 'systems'
    : q.match(/\b(?:personality|who am i|nature|strengths|pattern\w*)\b/) ? 'personality'
    : 'general';

  switch (topic) {
    case 'recommendation': {
      const rc = req.recommendationContext!;
      parts.push(
        `About ${rc.candidateDisplayName}: this came from calculated compatibility, not a guess.`,
      );
      const gradeBits = Object.entries(rc.grades)
        .slice(0, 4)
        .map(([k, v]) => `${k.replace(/([A-Z])/g, ' $1').toLowerCase()} ${v}`);
      if (gradeBits.length) parts.push(`Your pair grades: ${gradeBits.join(', ')}.`);
      if (rc.sharedInterests.length)
        parts.push(`You share an interest in ${rc.sharedInterests.slice(0, 3).join(', ')}.`);
      if (rc.rationale.length) parts.push(rc.rationale[0]);
      parts.push(
        'Compatibility signals are a starting point for a conversation — never proof someone is right or safe for you. If you would like, request the introduction and they can accept or decline privately.',
      );
      break;
    }
    case 'career': {
      if (west) {
        const saturnW = req.facts.find((f) => f.id === 'western.saturn.position' || f.id === 'stable.planet.saturn.tropicalSign');
        if (mcW) parts.push(`Your Midheaven — the point Western astrology reads as your public direction and what you want to be known for — is ${cite(used, mcW)}.`);
        if (saturnW) parts.push(`Saturn, the planet of slow effort and structure, is at ${cite(used, saturnW)}. It tends to describe where the work is genuinely hard, and where it compounds.`);
        const workAspect = req.facts.find((f) => f.id.startsWith('western.aspect.') && /saturn|jupiter/i.test(f.value));
        if (workAspect) parts.push(`One contact worth naming: ${cite(used, workAspect)}. An aspect is just an angle between two planets — this one is read as an ongoing conversation between those two drives.`);
        if (!mcW && !saturnW) parts.push('Your Western chart is not loaded yet, so I would only be guessing. Open the Charts tab and I can read career from your calculated placements.');
        break;
      }
      const d10 = req.facts.find((f) => f.id.includes('.d10') || f.id.includes('dasamsa'));
      const saturn = req.facts.find((f) => f.id === 'jyotish.saturn.position' || f.id === 'stable.planet.saturn.siderealSign');
      if (dasha) parts.push(`Right now you are in your ${cite(used, dasha)} — in Jyotish, career questions are read through this running period first.`);
      else if (dashaPoss) parts.push(`Because your birth time isn't exact, your running dasha has more than one candidate: ${cite(used, dashaPoss)}. I'll interpret what they share.`);
      if (d10) parts.push(`Your career varga (D10) shows ${cite(used, d10)}.`);
      if (saturn) parts.push(`Saturn — discipline and long arcs of work — sits at ${cite(used, saturn)}.`);
      parts.push('From the Western side, I would look at your Midheaven and Saturn/Jupiter aspects; ask me and I will walk through them.');
      break;
    }
    case 'relationship': {
      const venus = req.facts.find((f) => f.id === 'western.venus.position' || f.id === 'stable.planet.venus.tropicalSign');
      if (west) {
        if (moonW) parts.push(`Your Moon — how you need to be met emotionally — is ${cite(used, moonW)}.`);
        if (venus) parts.push(`Venus, the planet Western astrology reads as your relating style and your taste, is ${cite(used, venus)}.`);
        if (ascW) parts.push(`With ${cite(used, ascW)} rising — your rising sign is the way you arrive in a room, before anyone knows you — people likely meet a composed first layer before the deeper one.`);
        if (!moonW && !venus && !ascW) parts.push('I would need your calculated Western placements loaded before I read this.');
        parts.push(SAFETY_NOTE);
        break;
      }
      if (moonJ) parts.push(`Your Moon — how you need to be met emotionally — is ${cite(used, moonJ)}.`);
      if (moonRange) parts.push(`Your Moon may fall in more than one position depending on your exact birth time: ${cite(used, moonRange)}. Both readings share a strong feeling-first core, and I will hold both until your time is narrowed.`);
      if (venus) parts.push(`Venus, your relating style, is ${cite(used, venus)}.`);
      if (lagna) parts.push(`With ${cite(used, lagna)} rising, people likely meet a composed first layer before the deeper one.`);
      parts.push(SAFETY_NOTE);
      break;
    }
    case 'money': {
      if (west) {
        const jupW = req.facts.find((f) => f.id === 'western.jupiter.position' || f.id === 'stable.planet.jupiter.tropicalSign');
        if (jupW) parts.push(`Jupiter, read in Western astrology as expansion and appetite for risk, is ${cite(used, jupW)}.`);
        parts.push('I will not read markets, investments or a money decision from a chart — that is outside what astrology can responsibly answer. What it can describe is your relationship to security and spending. For concrete decisions, treat this as reflection, not financial advice.');
        break;
      }
      const jup = req.facts.find((f) => f.id === 'jyotish.jupiter.position' || f.id === 'stable.planet.jupiter.siderealSign');
      if (jup) parts.push(`Jupiter, the classical significator of growth and resources, is ${cite(used, jup)}.`);
      if (dasha) parts.push(`Your current ${cite(used, dasha)} colours how money themes are likely showing up now.`);
      parts.push('For concrete decisions, treat this as reflection — not financial advice.');
      break;
    }
    case 'timing': {
      if (west) {
        const hits = req.facts.filter((f) => f.system === 'western' && f.id.startsWith('transit.'));
        if (hits.length > 0) {
          parts.push('Western timing is read from transits — where the planets are right now against where they were when you were born. Currently:');
          for (const h of hits.slice(0, 4)) parts.push(`• ${cite(used, h)}`);
        } else {
          parts.push('Western timing is read from transits — where the planets are right now against where they were when you were born. Open the Charts tab and enable the transit overlay, and I can read them against your natal placements.');
        }
        break;
      }
      if (dasha) {
        parts.push(`You are currently in ${cite(used, dasha)}.`);
        const span = req.facts.find((f) => f.id === 'jyotish.dasha.mahadashaSpan');
        if (span) parts.push(`This mahadasha runs ${cite(used, span)}.`);
      } else if (dashaPoss) {
        parts.push(`With your birth-time window, the possible dasha readings are: ${cite(used, dashaPoss)}.`);
      }
      parts.push('Western timing would add current transits — ask "what are my transits" and I will read them from the calculated overlay.');
      break;
    }
    case 'transits': {
      // Same calculated overlay for both voices — only the label differs.
      const moving = factsBy(req.facts, 'gochar.', 'transit.').filter(
        (f) => f.system === (west ? 'western' : 'jyotish'),
      );
      if (moving.length > 0) {
        parts.push('From today\'s calculated positions:');
        for (const g of moving.slice(0, 4)) parts.push(`• ${cite(used, g)}`);
      } else if (west) {
        parts.push('Open the Charts tab and enable the transit overlay — then I can read today\'s transits against your natal chart.');
      } else {
        parts.push('Open the Charts tab and enable the transit overlay — then I can read today\'s gochar against your chart.');
      }
      break;
    }
    case 'uncertainty': {
      if (ascUnavailable) {
        parts.push(`Honest answer: ${cite(used, ascUnavailable)}`);
        parts.push(
          west
            ? 'You don\'t need to invent a birth time. Your Sun and most planetary placements are still solid, and rising-sign and house work unlocks when we narrow the window. The refinement flow in your profile can use dated life events to suggest a better window — as an estimate, never a certainty.'
            : 'You don\'t need to invent a birth time. Your Moon-based and planet-based readings are still solid, and rising-sign work unlocks when we narrow the window. The refinement flow in your profile can use dated life events to suggest a better window — as an estimate, never a certainty.',
        );
      } else {
        parts.push('Your chart is calculated with your stated time confidence, and I always mark which readings depend on it.');
      }
      break;
    }
    case 'systems': {
      parts.push('Jyotish uses the sidereal zodiac (fixed to the stars, offset today by roughly 24°) and leans on dashas and nakshatras for timing; Western astrology uses the tropical zodiac (fixed to the seasons) and leans on aspects, houses and transits for psychology and cycles. Same sky, different reference frames — which is why your Sun or Moon can carry different sign names in each.');
      // The one question where naming both traditions is the answer, whichever
      // voice is speaking.
      if (moonJ) parts.push(`For example, your sidereal Moon is ${cite(used, moonJ)}.`);
      if (moonW) parts.push(`while tropically it reads ${cite(used, moonW)}.`);
      parts.push(
        west
          ? 'You are in Astrally\'s Western reading right now. Jyotish is the app\'s main reading and it is one tap away.'
          : 'You are in Astrally\'s Jyotish reading, which is the app\'s main one. Western astrology is an optional mode you can switch on any time.',
      );
      break;
    }
    case 'personality': {
      if (west) {
        if (sunW) parts.push(`Starting with your Sun — ${cite(used, sunW)} — this is the drive Western astrology treats as your core motivation.`);
        if (moonW) parts.push(`Your Moon, the private and emotional layer, is ${cite(used, moonW)}.`);
        if (ascW) parts.push(`Your rising sign — the way you arrive before anyone knows you — is ${cite(used, ascW)}.`);
        const aspectW = req.facts.find((f) => f.id.startsWith('western.aspect.'));
        if (aspectW) parts.push(`One aspect stands out: ${cite(used, aspectW)}. An aspect is an angle between two planets, read as a standing conversation between those two drives.`);
        if (!sunW && !moonW && !ascW) parts.push('Your Western placements are not loaded yet — open the Charts tab and I can read them.');
        break;
      }
      if (moonJ) parts.push(`Starting from your Moon — ${cite(used, moonJ)} — your emotional processing likely runs steady and sensory rather than impulsive.`);
      if (lagna) parts.push(`Your lagna, ${cite(used, lagna)}, shapes the first impression you give.`);
      const yoga = req.facts.find((f) => f.id.startsWith('jyotish.yoga.'));
      if (yoga) parts.push(`Your chart also carries ${yoga.label}: ${cite(used, yoga)}.`);
      const aspect = req.facts.find((f) => f.id.startsWith('western.aspect.'));
      if (aspect) parts.push(`On the Western side, ${cite(used, aspect)} adds a distinct inner tension-and-drive pattern.`);
      break;
    }
    default: {
      if (west) {
        parts.push('I can read this through your calculated chart. A few places we could start:');
        if (sunW) parts.push(`• Your Sun: ${cite(used, sunW)}`);
        if (moonW) parts.push(`• Your Moon: ${cite(used, moonW)}`);
        if (ascW) parts.push(`• Your rising sign: ${cite(used, ascW)}`);
        if (ascUnavailable) parts.push(`• What your unknown birth time does and doesn't limit`);
        parts.push('Ask about personality, career, relationships, current transits, or the people I recommend.');
        break;
      }
      parts.push('I can read this through your calculated chart. A few places we could start:');
      if (moonJ) parts.push(`• Your Moon: ${cite(used, moonJ)}`);
      if (lagna) parts.push(`• Your lagna: ${cite(used, lagna)}`);
      if (dasha) parts.push(`• Your current period: ${cite(used, dasha)}`);
      if (ascUnavailable) parts.push(`• What your unknown birth time does and doesn't limit`);
      parts.push('Ask about personality, career, relationships, timing, or the people I recommend.');
    }
  }

  // Memory suggestions from explicit stable statements only.
  const profession = lastUser.match(/i(?:'m| am)? work(?:ing)? as an? ([a-z ]{3,30})/i) ??
    lastUser.match(/i(?:'m| am) an? ([a-z]{3,20}(?: engineer| designer| doctor| founder| teacher| analyst| manager| developer))/i);
  if (profession) {
    suggestions.push({
      category: 'profession',
      content: `Works as ${profession[1].trim()}`,
      confidence: 0.9,
      sensitive: false,
    });
  }
  const goal = lastUser.match(/(?:my goal is|i want to|i plan to) ([^.!?]{5,80})/i);
  if (goal) {
    suggestions.push({
      category: 'ambition',
      content: `Goal: ${goal[1].trim()}`,
      confidence: 0.75,
      sensitive: false,
    });
  }
  const hobby = lastUser.match(/i (?:love|enjoy|really like) ([^.!?]{3,50})/i);
  if (hobby) {
    suggestions.push({
      category: 'hobby',
      content: `Enjoys ${hobby[1].trim()}`,
      confidence: 0.8,
      sensitive: false,
    });
  }
  const health = q.match(/depress|anxiet|illness|diagnos|therapy/);
  if (health) {
    suggestions.push({
      category: 'recurring-concern',
      content: 'Mentioned a wellbeing concern (needs your confirmation to remember)',
      confidence: 0.4,
      sensitive: true,
    });
  }

  return {
    reply: parts.filter(Boolean).join('\n\n'),
    citedFactIds: [...used],
    memorySuggestions: suggestions,
    provider: 'mock',
  };
}
